/**
 * / — o painel fiscal. G7 §12.
 *
 * O QUE ELE MOSTRA, E O QUE ELE NÃO INVENTA
 *
 *   O §80 lista o conteúdo e termina com uma condição que decide o desenho
 *   inteiro: _"quando esses dados existirem"_.
 *
 *   Então cada cartão aqui conta linhas que já estão no banco. Não há meta, não
 *   há projeção, não há "faturamento do mês" — `nfse_documents` não guarda valor,
 *   e um painel que exibisse zero reais estaria afirmando algo falso sobre uma
 *   empresa que faturou.
 *
 * `unknown_state` TEM CARTÃO PRÓPRIO
 *
 *   É o estado mais importante deste produto e o que o legado não tinha. Um
 *   documento em `unknown_state` é uma transmissão cujo desfecho ninguém
 *   conhece — e conhecer a quantidade deles é a diferença entre operar com
 *   consciência do risco e operar achando que está tudo certo.
 *
 *   Ele fica ao lado de `cancel_requested` pelo mesmo motivo: os dois são
 *   pendências que exigem ação humana, e nenhum se resolve sozinho.
 */

import { useQuery } from '@tanstack/react-query';
import { useSession } from '../lib/session.js';
import {
  fetchArtifacts,
  fetchCertificates,
  fetchEvents,
  fetchNfeDocuments,
  fetchNfseDocuments,
} from '../lib/queries.js';
import { ErrorState, LoadingState, dataBr } from '../components/States.js';

/** Estados que exigem alguém olhando. */
const PENDENTES = new Set(['transmitting', 'cancel_requested', 'awaiting_distribution', 'signed']);
const INDETERMINADOS = new Set(['unknown_state']);

function Card({
  rotulo,
  valor,
  nota,
  tom = 'neutro',
}: {
  rotulo: string;
  valor: number | string;
  nota?: string;
  tom?: 'neutro' | 'atencao' | 'alerta';
}) {
  return (
    <div className={`of-card of-card--${tom}`}>
      <div className="of-card__valor">{valor}</div>
      <div className="of-card__rotulo">{rotulo}</div>
      {nota !== undefined && <div className="of-card__nota">{nota}</div>}
    </div>
  );
}

export function DashboardPage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';

  const nfse = useQuery({
    queryKey: ['nfse', id],
    queryFn: () => fetchNfseDocuments(id),
    enabled: id !== '',
  });
  const nfe = useQuery({
    queryKey: ['nfe', id],
    queryFn: () => fetchNfeDocuments(id),
    enabled: id !== '',
  });
  const certs = useQuery({
    queryKey: ['certificados', id],
    queryFn: () => fetchCertificates(id),
    enabled: id !== '',
  });
  const artefatos = useQuery({
    queryKey: ['artefatos', id],
    queryFn: () => fetchArtifacts(id),
    enabled: id !== '',
  });
  const eventos = useQuery({
    queryKey: ['eventos', id],
    queryFn: () => fetchEvents(id),
    enabled: id !== '',
  });

  const consultas = [nfse, nfe, certs, artefatos, eventos];

  if (consultas.some((c) => c.isPending)) return <LoadingState label="Carregando o painel…" />;

  /*
   * Uma consulta falhada derruba o painel inteiro, de propósito.
   *
   * Um dashboard com quatro cartões certos e um errado é pior do que um
   * dashboard que não carrega: quem olha não tem como saber qual dos cinco
   * números está mentindo.
   */
  const falha = consultas.find((c) => c.isError);
  if (falha !== undefined) {
    return <ErrorState title="Painel fiscal" error={falha.error} />;
  }

  const docsNfse = nfse.data ?? [];
  const docsNfe = nfe.data ?? [];
  const certificados = certs.data ?? [];

  const conta = (rows: readonly { status: string }[], conjunto: Set<string>) =>
    rows.filter((r) => conjunto.has(r.status)).length;

  const pendentes = conta(docsNfse, PENDENTES) + conta(docsNfe, PENDENTES);
  const indeterminados = conta(docsNfse, INDETERMINADOS) + conta(docsNfe, INDETERMINADOS);

  const emitidos =
    docsNfse.filter((d) => d.status === 'authorized' || d.status === 'awaiting_distribution')
      .length + docsNfe.filter((d) => d.status === 'authorized').length;

  /* Certificados a vencer em 30 dias. O prazo é o do produto, não do usuário. */
  const limite = Date.now() + 30 * 24 * 60 * 60 * 1000;
  const vencendo = certificados.filter(
    (c) => c.status === 'active' && new Date(c.not_after).getTime() <= limite,
  );
  const proximo = certificados
    .filter((c) => c.status === 'active')
    .sort((a, b) => new Date(a.not_after).getTime() - new Date(b.not_after).getTime())[0];

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Painel Fiscal</h1>
        <p className="of-page__subtitle">
          {selected?.legal_name} · <span className="of-mono">{selected?.tax_id}</span>
        </p>
      </header>

      <div className="of-cards">
        <Card rotulo="NFS-e" valor={docsNfse.length} nota="documentos registrados" />
        <Card rotulo="NF-e" valor={docsNfe.length} nota="documentos registrados" />
        <Card rotulo="Emitidos" valor={emitidos} nota="autorizados ou aguardando distribuição" />
        <Card
          rotulo="Pendentes"
          valor={pendentes}
          nota="exigem acompanhamento"
          tom={pendentes > 0 ? 'atencao' : 'neutro'}
        />
        <Card
          rotulo="Estado indeterminado"
          valor={indeterminados}
          nota="desfecho desconhecido — nunca retransmitir"
          tom={indeterminados > 0 ? 'alerta' : 'neutro'}
        />
        <Card rotulo="Eventos" valor={(eventos.data ?? []).length} nota="registrados" />
        <Card rotulo="Artefatos custodiados" valor={(artefatos.data ?? []).length} />
        <Card
          rotulo="Certificados a vencer"
          valor={vencendo.length}
          nota={proximo ? `próximo: ${dataBr(proximo.not_after)}` : 'nenhum ativo'}
          tom={vencendo.length > 0 ? 'atencao' : 'neutro'}
        />
      </div>

      <p className="of-page__footnote">
        Os números contam linhas persistidas nesta identidade fiscal. O painel não projeta, não
        estima e não mostra valor financeiro — <code className="of-mono">nfse_documents</code> não
        guarda valor, e um total em reais aqui seria invenção.
      </p>
    </section>
  );
}
